import { TokenKind } from './types';

// Arabic marks: Harakat U+064B-U+065F, superscript alef U+0670, tatweel U+0640, Quranic U+06D6-U+06ED
const ARABIC_MARKS_REGEX = /[\u064B-\u065F\u0670\u0640\u06D6-\u06ED]/g;

// Bidi & format controls: U+200B-U+200F, U+202A-U+202E, U+2066-U+2069, U+FEFF
const BIDI_FORMAT_REGEX = /[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;

// Digit mappings
const ARABIC_INDIC_DIGITS: Record<string, string> = {
  '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
  '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9',
  '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
  '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
};

const DIGIT_REGEX = /[\u0660-\u0669\u06F0-\u06F9]/g;

/**
 * Normalizes text strictly for matching purposes.
 * NEVER applied to display text.
 */
export function normalizeForMatching(text: string): string {
  // 1. Remove format and bidi controls
  let s = text.replace(BIDI_FORMAT_REGEX, '');

  // 2. Remove Arabic marks, tatweel, quranic annotations
  s = s.replace(ARABIC_MARKS_REGEX, '');

  // 3. Map Arabic-Indic and Extended digits to 0-9
  s = s.replace(DIGIT_REGEX, (ch) => ARABIC_INDIC_DIGITS[ch] ?? ch);

  // 4. Character transformations
  let result = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    // Unify Alefs: أ إ آ ٱ -> ا
    if (ch === 'أ' || ch === 'إ' || ch === 'آ' || ch === 'ٱ') {
      result += 'ا';
    }
    // Unify Yaa: ى -> ي
    else if (ch === 'ى') {
      result += 'ي';
    }
    // Unify Persian/Urdu variants: ک -> ك, ی -> ي, ھ/ہ/ۂ -> ه
    else if (ch === 'ک') {
      result += 'ك';
    } else if (ch === 'ی') {
      result += 'ي';
    } else if (ch === 'ھ' || ch === 'ہ' || ch === 'ۂ') {
      result += 'ه';
    }
    // Latin lowercase
    else if (ch >= 'A' && ch <= 'Z') {
      result += ch.toLowerCase();
    }
    // Whitespace: collapse into single space
    else if (/\s/.test(ch)) {
      if (!result.endsWith(' ') && result.length > 0) {
        result += ' ';
      }
    }
    // Strip punctuation and symbols for matching
    else if (isPunctuationOrSymbol(ch)) {
      // omit
    } else {
      result += ch;
    }
  }

  return result.trim();
}

/**
 * Checks if a character is punctuation or symbol that should be stripped in matching
 */
export function isPunctuationOrSymbol(ch: string): boolean {
  // Standard ASCII punctuation & Arabic punctuation & quotes (excluding whitespace)
  return /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~،؛؟«»“”‘’—–…]/.test(ch);
}

/**
 * Classifies a token string based on its dominant character type
 */
export function classifyToken(raw: string): TokenKind {
  const norm = normalizeForMatching(raw);
  if (!norm) return 'other';

  if (/^[0-9]+$/.test(norm)) {
    return 'number';
  }
  if (/^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(norm)) {
    return 'arabic';
  }
  if (/^[a-zA-Z]/.test(norm)) {
    return 'latin';
  }
  return 'other';
}
